import React, { useState } from 'react'
import { useMediaQuery, useTheme, Divider, Menu, Collapse, List } from '@mui/material'
import { ReportsDropdown, StyledMenuItem, StyledGroupName } from './styles'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { getPagePermissions } from '../../../store/slices/roleSlice/selectors'
import { useQueryClient } from '@tanstack/react-query'
import { PreparedRouteKeys } from '../../../hooks/useAllowedPath'
import { useNavigate } from 'react-router-dom'
import ROUTES from '../../../consts/paths'
import { StyledListItemButton, StyledListItemText } from '../MobileTabMenu/style'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { ROLES } from '../../../consts/roles'
import { isReportNavRole } from '../../../helpers/roleHelpers'
import { InstitutionDropdown } from '../AdminMenuItems/styles'
import { useUserProfile } from '../../../hooks/useUserProfile'

interface LinkProps {
  label: string
  link: string
}

interface MenuItemProps {
  group?: string
  links: LinkProps[]
}

const renderMenuList = (
  filteredMenuItems: MenuItemProps[] | undefined,
  currentPath: string,
  handleMenuItemClick: (route: string) => void
): JSX.Element => {
  return (
    <>
      {filteredMenuItems?.map((item: MenuItemProps, index: number) => (
        <>
          {(item?.group != null) &&
            <StyledGroupName tabIndex={1}>
              {item.group}
            </StyledGroupName>}

          {item?.links?.map((link: LinkProps, linkIndex: number) => {
            return (
              <StyledMenuItem
                tabIndex={1}
                active={link?.link === currentPath}
                key={linkIndex}
                onClick={() => { handleMenuItemClick(link.link) }}
              >
                {link.label}
              </StyledMenuItem>
            )
          })}
          {(item?.group != null && filteredMenuItems?.length - 1 !== index) && <Divider />}
        </>
      ))}
    </>
  )
}

const ReportMenuItems: React.FC = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.only('sm'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const currentPath = window.location.pathname
  const isReportsActive = currentPath.includes('/reports') || currentPath.includes('/instructor/surveys')
  const pagePermissions = getPagePermissions()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isOpenForMobile, setIsOpenForMobile] = useState<boolean>(false)
  const showAdminViewDashboard = pagePermissions?.includes(PreparedRouteKeys.GenericRoleSettings)
  const isAdminViewDashboardActive = currentPath === ROUTES.ADMIN_VIEWER_DASHBOARD

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = (): void => {
    setAnchorEl(null)
  }

  const queryClient = useQueryClient()
  const divisionAndSections = queryClient.getQueryData(['topBarDivisions'])
  // const currentUserRoles = queryClient.getQueryData(['currentUserRoles']) as UserRole[] || []
  const { data: userProfile } = useUserProfile()
  const currentUserRoles = userProfile?.roles ?? []

  const handleMenuItemClick = (route: string): void => {
    navigate(route)
    handleCloseMenu()
  }

  const canShowReportsForRole = (roleName: string | undefined): boolean =>
    roleName === ROLES.ACCOUNT_OWNER || isReportNavRole(roleName)

  const hasReportsRole = currentUserRoles.some(role => canShowReportsForRole(role.roleName))

  // Get current user role to determine what to show
  const currentRoleId = localStorage.getItem('currentRoleId')
  const currentRole = currentUserRoles.find(role => role.roleId === currentRoleId)

  // Show Reports for Account Owner, Owner, Viewer (but NOT System Manager, Editor, Primary Instructor, Student)
  const shouldShowReports = hasReportsRole && canShowReportsForRole(currentRole?.roleName)

  const preparedAdminRoutes = Array.isArray(divisionAndSections)
    ? divisionAndSections.map((division: any) => ({
      label: division?.title ?? 'Division',
      link: `${ROUTES.REPORTS}/${division?.divisionId}`
    }))
    : []

  const checkMyCoursePath = pagePermissions?.includes(PreparedRouteKeys.InstructorCourseListPage)
  const menuItems: MenuItemProps[] = []

  if (checkMyCoursePath) {
    menuItems.push({
      group: 'Instructor',
      links: [
        {
          label: 'My Courses',
          link: ROUTES.INSTRUCTOR_SURVEYS
        }
      ]
    })
  }

  if (preparedAdminRoutes?.length) {
    menuItems.push({
      group: 'Admin',
      links: preparedAdminRoutes
    })
  }

  const shouldShowAsDropdown = shouldShowReports && Array.isArray(divisionAndSections) && divisionAndSections.length > 1
  const shouldShowAsButton = shouldShowReports && Array.isArray(divisionAndSections) && divisionAndSections.length === 1

  return (
    <>

   {showAdminViewDashboard && (
           <>
             <InstitutionDropdown
               isMobile={isMobile}
               isTablet={isTablet}
               active={isAdminViewDashboardActive}
               onClick={() => { navigate(ROUTES.ADMIN_VIEWER_DASHBOARD) }}
             >
               Home
             </InstitutionDropdown>
           </>
   )}
      {

        !isMobile && shouldShowReports &&
        <>
          {shouldShowAsDropdown && (
            <>
              <ReportsDropdown
                disabled={!pagePermissions || !menuItems?.length} isMobile={isMobile} isTablet={isTablet} active={isReportsActive}
                onClick={(e) => { handleOpenMenu(e) }}>
                Reports <ExpandMoreRoundedIcon />
              </ReportsDropdown>

              <Menu
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
                disableScrollLock
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left'
                }}
                slotProps={{
                  paper: {
                    style: {
                      padding: `${theme.spacing(2)} 0`
                    }
                  }
                }}
                MenuListProps={{
                  style: {
                    padding: 0,
                    minWidth: theme.spacing(60),
                    borderRadius: theme.spacing(2)
                  }
                }}
                anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} elevation={3}>
                {renderMenuList(menuItems, currentPath, handleMenuItemClick)}
              </Menu >
            </>
          )}

          {shouldShowAsButton && (
            <ReportsDropdown
              disabled={!pagePermissions} isMobile={isMobile} isTablet={isTablet} active={isReportsActive}
              onClick={() => {
                const singleDivision = divisionAndSections[0]
                if (singleDivision) {
                  navigate(`${ROUTES.REPORTS}/${singleDivision.divisionId}`)
                }
              }}>
              Reports
            </ReportsDropdown>
          )}
        </>
      }

      {
        isMobile && shouldShowReports &&
        <>
          <StyledListItemButton onClick={() => { setIsOpenForMobile(!isOpenForMobile) }} active={isOpenForMobile} >
            <StyledListItemText primary="Reports" />
            {isOpenForMobile ? <ExpandLess /> : <ExpandMore />}
          </StyledListItemButton>
          <Collapse in={isOpenForMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderMenuList(menuItems, currentPath, handleMenuItemClick)}
            </List>
          </Collapse>
        </>
      }
    </>
  )
}

export default ReportMenuItems
